/*
 * Decléor — product rating badge (Judge.me preview badge) restructure
 * ---------------------------------------------------------------------------
 * Turns Judge.me's single flat string
 *     5.0 (19 avis)
 * into parts the stylesheet can treat differently
 *     5.0  /5  -  19 AVIS
 * i.e. score semibold, "/5" smaller, and a dash instead of the parentheses. See the
 * "Preview badge" block in snippets/judgeme-style.liquid for how each part is set.
 *
 * WHY THIS IS JAVASCRIPT AND NOT A JUDGE.ME SETTING.
 * The text comes from the app's own `badge_n_reviews_text` template (currently
 * "{{ average_rating_1_decimal }} ({{ n }} avis)"), so the dash and the "/5" COULD be done by
 * editing that one field in the Judge.me admin. What that cannot do is give the parts different
 * type: a single template string produces a single text node, and the reference design needs the
 * score, the "/5" and the review count each styled differently. That needs elements, so it needs
 * a script. The trade-off is deliberate and it is the only reason this file exists.
 *
 * IT IS LOCALE-PROOF WITHOUT ANY LOCALE FILE.
 * The review word is not hardcoded — it is taken from whatever Judge.me already rendered, so
 * "avis" / "reviews" / "Bewertungen" all survive, in all six storefront locales, and a merchant
 * who edits the wording in the Judge.me admin keeps their wording. The score is likewise reused
 * verbatim rather than reformatted, so a locale that renders "4,0" keeps its comma.
 *
 * Bails out and leaves the badge exactly as Judge.me rendered it if anything is unexpected:
 * no badge, zero reviews, or a text template that does not start with the score.
 */
(function () {
  'use strict';

  var HOST = '.jdgm-preview-badge';
  var BADGE = '.jdgm-preview-badge .jdgm-prev-badge';
  var TEXT = '.jdgm-prev-badge__text';
  var DONE = 'dc-rating__score';

  // Hoisted: restructure() is driven by a MutationObserver, so these would otherwise be
  // recompiled on every DOM change in the badge.
  var RE_SCORE = /^([0-9]+(?:[.,][0-9]+)?)\s*(.*)$/;
  var RE_OPEN_PAREN = /^\s*\(\s*/;
  var RE_CLOSE_PAREN = /\s*\)\s*$/;
  // If the merchant later edits Judge.me's badge template toward this same design (adding "/5"
  // and a dash there instead of leaving it to us), those tokens would arrive inside `rest` and we
  // would print them twice — "4.0/5/5 - - 467 avis". Strip them so both routes converge.
  var RE_LEAD_OUTOF = /^\s*\/\s*5\b\s*/;
  var RE_LEAD_SEP = /^\s*[-–—·|]\s*/;
  // Discriminator for a template with NO average, e.g. "19 avis": the leading number would then
  // be the review COUNT, and we would print it as the score. If what follows holds no digits,
  // there is no count left over — so the number we took was the count. Bail.
  var RE_DIGIT = /[0-9]/;

  function restructure(badge) {
    var textEl = badge.querySelector(TEXT);
    if (!textEl) {
      return;
    }
    // Idempotent: our own write re-triggers the observer, and Judge.me may re-render at any time.
    if (textEl.querySelector('.' + DONE)) {
      return;
    }
    // The app hides the badge entirely at 0 reviews; guard anyway so "Aucun avis" is never
    // mangled into a score.
    var n = badge.getAttribute('data-number-of-reviews');
    if (!n || n === '0') {
      return;
    }

    var raw = (textEl.textContent || '').trim();
    // Leading number = the score; everything after it = the count and its word.
    var parts = raw.match(RE_SCORE);
    if (!parts) {
      return; // unrecognised template — leave it alone rather than guess
    }
    var score = parts[1];
    var rest = parts[2].replace(RE_OPEN_PAREN, '').replace(RE_CLOSE_PAREN, '').trim();
    rest = rest.replace(RE_LEAD_OUTOF, '').replace(RE_LEAD_SEP, '').trim();
    if (!rest || !RE_DIGIT.test(rest)) {
      return;
    }

    function span(cls, text) {
      var el = document.createElement('span');
      el.className = cls;
      el.textContent = text;
      return el;
    }

    textEl.textContent = '';
    textEl.appendChild(span(DONE, score));
    textEl.appendChild(span('dc-rating__outof', '/5'));
    // aria-hidden: the dash is a visual separator, not something to read out.
    var sep = span('dc-rating__sep', '-');
    sep.setAttribute('aria-hidden', 'true');
    textEl.appendChild(sep);
    textEl.appendChild(span('dc-rating__count', rest));
  }

  function scan() {
    var badges = document.querySelectorAll(BADGE);
    var i;
    for (i = 0; i < badges.length; i += 1) {
      restructure(badges[i]);
    }
  }

  /* Judge.me renders the badge from its own script AFTER this file runs — the markup ships with
   * `style="display:none"` and `.jdgm-prev-badge__text { visibility: hidden }` until the app has
   * localised it — so one pass at load is not enough.
   *
   * Observed on the BADGE HOSTS, not on document.documentElement. The host block is
   * server-rendered (it is a theme-app-extension block, present in the initial HTML), so there is
   * something to attach to straight away, and a document-wide subtree observer in this theme would
   * wake on every cart drawer update, AOS reveal and Lenis class change for nothing.
   * characterData is included because the app may rewrite the existing text node in place rather
   * than replacing elements. The idempotency guard in restructure() is what stops our own write
   * from looping. */
  var observers = [];

  function watch() {
    var i;
    for (i = 0; i < observers.length; i += 1) {
      observers[i].disconnect();
    }
    observers = [];

    scan();

    if (typeof MutationObserver !== 'function') {
      return;
    }
    var hosts = document.querySelectorAll(HOST);
    var mo;
    for (i = 0; i < hosts.length; i += 1) {
      mo = new MutationObserver(scan);
      mo.observe(hosts[i], { childList: true, subtree: true, characterData: true });
      observers.push(mo);
    }
  }

  watch();

  // Theme editor: the product section (and with it the app block) can be re-rendered.
  document.addEventListener('shopify:section:load', watch);
})();
